import { type PreparedShape, type Rng, type ShapeDefinition, type ShapeSink } from '../shape.js';
import { hash01, onDisc, ramp, ridges, TAU } from './helpers.js';

/* ── River ─────────────────────────────────────────────────────────────── */

const RIVER_LENGTH = 34;

function riverCentre(z: number, wiggle: number): number {
  return Math.sin(z * 0.16) * (3.4 + wiggle * 2.6) + Math.sin(z * 0.37 + 1.1) * 1.5;
}

function riverWidth(z: number, wiggle: number): number {
  return 2.3 + Math.sin(z * 0.21 + 0.7) * 0.7 + wiggle * 0.9;
}

export const river: ShapeDefinition = {
  name: 'river',
  palette: { base: 0x1e_5c_8c, shade: 0x3c_7a_4e, accent: 0xe8_f8_ff },
  driftRate: 1.05,
  framing: 2.03,
  prepare: ({ random, total, variant }): PreparedShape => {
    const wiggle = variant;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const z = (random() - 0.5) * RIVER_LENGTH;
      const centre = riverCentre(z, wiggle);
      const width = riverWidth(z, wiggle);
      if (p < 0.5) {
        // Water surface: a flat sheet with a travelling ripple.
        const lateral = (random() * 2 - 1) * width;
        sink.x = centre + lateral;
        sink.y = Math.sin(z * 0.9 + lateral * 0.6) * 0.09;
        sink.z = z;
        const edge = Math.abs(lateral) / width;
        sink.tint = edge > 0.82 ? 0.9 + random() * 0.1 : random() * 0.14;
        sink.driftZ = 0.42;
        sink.driftY = 0.1;
        sink.phase = z * 0.55;
      } else if (p < 0.86) {
        // Valley walls climbing away from both banks.
        const away = (0.4 + random() * 6.4) * (random() < 0.5 ? -1 : 1);
        sink.x = centre + Math.sign(away) * width + away;
        sink.z = z;
        const climb = ramp(Math.abs(away), 0.2, 6.5);
        sink.y = climb * (1.5 + ridges(sink.x, z) * 0.9) - 0.15;
        sink.tint = 0.45 + climb * 0.14 + random() * 0.06;
      } else if (p < 0.96) {
        // Reeds standing in the shallows, swaying at the tip.
        const side = random() < 0.5 ? -1 : 1;
        const height = 0.5 + random() * 1.1;
        const up = random();
        sink.x = centre + side * (width * (0.86 + random() * 0.3));
        sink.y = up * height;
        sink.z = z;
        sink.tint = 0.5 + up * 0.2;
        sink.driftX = up * up * 0.3;
        sink.phase = z * 0.4 + hash01(index) * TAU;
      } else {
        // Mist drifting over the water.
        sink.x = centre + (random() * 2 - 1) * width * 1.4;
        sink.y = 0.25 + random() * 1.5;
        sink.z = z;
        sink.tint = 0.95;
        sink.driftX = 0.6;
        sink.driftY = 0.2;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Ocean ─────────────────────────────────────────────────────────────── */

const SWELL = 22;

function swellHeight(x: number, z: number, steep: number): number {
  return (
    Math.sin(x * 0.28 + z * 0.11) * (1.1 + steep) +
    Math.sin(x * 0.13 - z * 0.34 + 2.1) * 0.8 +
    Math.sin((x + z) * 0.61 - 1.3) * 0.28
  );
}

export const ocean: ShapeDefinition = {
  name: 'ocean',
  palette: { base: 0x14_48_6e, shade: 0x2a_9a_b0, accent: 0xf0_ff_ff },
  driftRate: 0.8,
  framing: 1.82,
  prepare: ({ random, total, variant }): PreparedShape => {
    const steep = variant * 0.9;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const x = (random() - 0.5) * SWELL * 2;
      const z = (random() - 0.5) * SWELL * 2;
      const height = swellHeight(x, z, steep);
      sink.x = x;
      sink.z = z;
      sink.phase = x * 0.28 + z * 0.11;
      if (p < 0.9) {
        sink.y = height;
        // Crests foam, troughs stay deep — the tint is the wave's own slope.
        const crest = ramp(height, 0.7 + steep, 1.9 + steep);
        sink.tint = crest > 0.05 ? 0.35 + crest * 0.5 : random() * 0.18;
        sink.driftY = 0.55 + crest * 0.5;
        sink.driftX = 0.25;
      } else {
        // Spray torn off the highest crests.
        sink.y = height + 0.3 + random() * 1.4;
        sink.tint = 0.92 + random() * 0.08;
        sink.driftY = 0.9;
        sink.driftX = 0.7;
      }
    };
    return { place };
  },
};

/* ── Rain ──────────────────────────────────────────────────────────────── */

const RAIN_STREAKS = 900;

/**
 * One slanted streak of drops. Grouping drops into streaks is the whole trick:
 * scattered points read as fog, but a line of them leaning the same way as its
 * neighbours reads immediately as rain.
 */
function rainStreak(sink: ShapeSink, random: Rng, slant: number): void {
  const streak = Math.floor(random() * RAIN_STREAKS);
  const along = random();
  const head = hash01(streak) * 15;
  const y = (head + along * 3.5) % 15;
  sink.x = (hash01(streak * 3.1) - 0.5) * 34 + y * slant;
  sink.z = (hash01(streak * 7.7) - 0.5) * 34;
  sink.y = y;
  // Brightest at the leading drop, tailing off behind it.
  sink.tint = 0.35 + (1 - along) * 0.6;
  sink.driftY = 1.4 + hash01(streak * 11.3) * 1.1;
  sink.driftX = sink.driftY * slant * 0.5;
  sink.phase = streak * 0.7;
}

export const rain: ShapeDefinition = {
  name: 'rain',
  palette: { base: 0x3a_4e_6e, shade: 0x6e_8a_b0, accent: 0xd8_ec_ff },
  driftRate: 2.2,
  framing: 1.76,
  prepare: ({ random, total, variant }): PreparedShape => {
    const slant = -0.12 - variant * 0.3;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.56) {
        rainStreak(sink, random, slant);
      } else if (p < 0.8) {
        // Wet ground, ringed where the drops land.
        onDisc(sink, random, 17);
        const r = Math.hypot(sink.x, sink.z);
        const ring = Math.abs(Math.sin(r * 2.6 - 1));
        sink.y = ring * 0.09;
        sink.tint = 0.05 + ring * ring * 0.75;
        sink.driftY = 0.16;
        sink.phase = r * 2.6;
      } else {
        // The cloud ceiling, with a defined underside.
        sink.x = (random() - 0.5) * 40;
        sink.z = (random() - 0.5) * 40;
        const lump = ridges(sink.x * 0.6, sink.z * 0.6);
        sink.y = 15.5 + random() * 3.5 + lump * 1.2;
        sink.tint = 0.1 + Math.max(0, lump) * 0.28 + random() * 0.1;
        sink.driftX = 0.5;
        sink.driftZ = 0.35;
        sink.phase = sink.x * 0.1 + sink.z * 0.07;
      }
    };
    return { place };
  },
};
